// app/analytics/ScrollDepth.tsx

/**
 * ---------------------------------------------------------------------------
 * <ScrollDepth /> - GA4 scroll-depth milestones for the App Router
 * ---------------------------------------------------------------------------
 * Fires a `scroll_depth` event at 25 %, 50 %, 75 %, and 90 % of the page
 * height.  Each milestone fires at most once per page load.  When the
 * pathname changes (client-side navigation) the milestone set is reset so
 * the new page gets its own independent measurements.
 *
 * Implementation notes
 * • Uses a passive RAF-throttled scroll listener - no external deps.
 * • No-ops immediately when `GA_ID` is absent (local dev / tests).
 * • Returns null - side-effect only.
 * ---------------------------------------------------------------------------
 */

"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { event, GA_ID } from "@/lib/gtag";

const MILESTONES = [25, 50, 75, 90] as const;

export default function ScrollDepth() {
  const pathname = usePathname();
  // Tracks which milestones have already been fired for the current page.
  const firedRef = useRef<Set<number>>(new Set());

  // Reset milestones on every pathname change (including the initial mount).
  useEffect(() => {
    firedRef.current = new Set();
  }, [pathname]);

  useEffect(() => {
    if (!GA_ID) return;

    let rafPending = false;

    const checkDepth = () => {
      rafPending = false;

      const scrolled = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      // Avoid dividing by zero on very short pages.
      if (docHeight <= 0) return;

      const pct = (scrolled / docHeight) * 100;

      for (const milestone of MILESTONES) {
        if (!firedRef.current.has(milestone) && pct >= milestone) {
          firedRef.current.add(milestone);
          event("scroll_depth", { percent: milestone });
        }
      }
    };

    const onScroll = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(checkDepth);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Run once on mount in case the page is short enough that 25 % is already
    // visible without any scrolling (e.g. 404 pages, short landing sections).
    checkDepth();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  return null;
}
